/**
 * usePermissions Composable
 *
 * Provides reactive permission checks for Vue components
 * Supports hierarchical permissions (.view = read-only, .edit = full access)
 *
 * @example
 * ```vue
 * <script setup>
 * const { can, canView, canEdit, canAny, canAll, isRole } = usePermissions();
 *
 * const canViewUsers = canView('users'); // checks users.view or users.edit
 * const canEditUsers = canEdit('users'); // checks users.edit only
 * const canCreateOrEdit = canAny(['jobs.editAll', 'results.editAll']);
 * const isAdmin = isRole('Admin');
 * </script>
 * ```
 */

import { useAuthStore } from "@/stores/auth";
import { useRolesStore } from "@/stores/roles";
import { computed, type ComputedRef } from "vue";

export interface PermissionsComposable {
  /**
   * Check if user has specific permission
   */
  can: (permission: string) => ComputedRef<boolean>;

  /**
   * Check if user can view a resource (has .view or .edit permission)
   * Hierarchical: .edit automatically includes .view rights
   */
  canView: (resource: string) => ComputedRef<boolean>;

  /**
   * Check if user can edit a resource (has .edit permission)
   * Implies create, update, delete rights
   */
  canEdit: (resource: string) => ComputedRef<boolean>;

  /**
   * Check if user has any of the specified permissions
   */
  canAny: (permissions: string[]) => ComputedRef<boolean>;

  /**
   * Check if user has all of the specified permissions
   */
  canAll: (permissions: string[]) => ComputedRef<boolean>;

  /**
   * Check if user has specific role
   */
  isRole: (roleName: string) => ComputedRef<boolean>;

  /**
   * Get all user permissions as array
   */
  userPermissions: ComputedRef<string[]>;

  /**
   * Get user role name
   */
  userRole: ComputedRef<string | null>;
}

export function usePermissions(): PermissionsComposable {
  const authStore = useAuthStore();
  const rolesStore = useRolesStore();

  /**
   * Get user's role with permissions
   */
  const getUserRole = () => {
    if (!authStore.user) return null;
    return rolesStore.getRoleById(authStore.user.role_id);
  };

  /**
   * Get all user permissions as array
   */
  const userPermissions = computed((): string[] => {
    const role = getUserRole();
    return role?.permissions.map((p) => p.name) ?? [];
  });

  /**
   * Get user role name
   */
  const userRole = computed((): string | null => {
    return getUserRole()?.name ?? null;
  });

  /**
   * Check if user has specific permission
   */
  const can = (permission: string): ComputedRef<boolean> => {
    return computed(() => {
      if (!authStore.isAuthenticated) return false;
      return userPermissions.value.includes(permission);
    });
  };

  /**
   * Check if user can view a resource (ANY level)
   * Hierarchical: .editAll > .viewAll > .editOwn > .viewOwn
   *
   * @param resource - Resource name (e.g., 'jobs', 'users')
   * @param scope - 'own' (viewOwn) or 'all' (viewAll), default 'own'
   * @example
   * canView('jobs', 'own') checks for viewOwn, editOwn, viewAll, or editAll
   * canView('jobs', 'all') checks for viewAll or editAll
   */
  const canView = (
    resource: string,
    scope: "own" | "all" = "own",
  ): ComputedRef<boolean> => {
    return computed(() => {
      if (!authStore.isAuthenticated) return false;

      if (scope === "all") {
        // View all: requires .viewAll or .editAll
        return (
          userPermissions.value.includes(`${resource}.viewAll`) ||
          userPermissions.value.includes(`${resource}.editAll`)
        );
      } else {
        // View own: viewOwn, editOwn, viewAll, or editAll
        return (
          userPermissions.value.includes(`${resource}.viewOwn`) ||
          userPermissions.value.includes(`${resource}.editOwn`) ||
          userPermissions.value.includes(`${resource}.viewAll`) ||
          userPermissions.value.includes(`${resource}.editAll`)
        );
      }
    });
  };

  /**
   * Check if user can edit a resource
   * Hierarchical: .editAll > .editOwn
   *
   * @param resource - Resource name (e.g., 'jobs', 'users')
   * @param scope - 'own' (editOwn) or 'all' (editAll), default 'own'
   * @example
   * canEdit('jobs', 'own') checks for editOwn or editAll
   * canEdit('jobs', 'all') checks for editAll only
   */
  const canEdit = (
    resource: string,
    scope: "own" | "all" = "own",
  ): ComputedRef<boolean> => {
    return computed(() => {
      if (!authStore.isAuthenticated) return false;

      if (scope === "all") {
        // Edit all: requires .editAll
        return userPermissions.value.includes(`${resource}.editAll`);
      } else {
        // Edit own: editOwn or editAll
        return (
          userPermissions.value.includes(`${resource}.editOwn`) ||
          userPermissions.value.includes(`${resource}.editAll`)
        );
      }
    });
  };

  /**
   * Check if user has any of the specified permissions
   */
  const canAny = (permissions: string[]): ComputedRef<boolean> => {
    return computed(() => {
      if (!authStore.isAuthenticated) return false;
      return permissions.some((permission) => {
        if (userPermissions.value.includes(permission)) {
          return true;
        }

        const [resource, action] = permission.split(".");
        if (!resource || !action) return false;

        // Hierarchical fallbacks
        if (action === "viewAll") {
          return userPermissions.value.includes(`${resource}.editAll`);
        }

        if (action === "viewOwn") {
          return (
            userPermissions.value.includes(`${resource}.editOwn`) ||
            userPermissions.value.includes(`${resource}.viewAll`) ||
            userPermissions.value.includes(`${resource}.editAll`)
          );
        }

        if (action === "editOwn") {
          return userPermissions.value.includes(`${resource}.editAll`);
        }

        return false;
      });
    });
  };

  /**
   * Check if user has all of the specified permissions
   */
  const canAll = (permissions: string[]): ComputedRef<boolean> => {
    return computed(() => {
      if (!authStore.isAuthenticated) return false;
      return permissions.every((p) => userPermissions.value.includes(p));
    });
  };

  /**
   * Check if user has specific role
   */
  const isRole = (roleName: string): ComputedRef<boolean> => {
    return computed(() => {
      if (!authStore.isAuthenticated) return false;
      return userRole.value === roleName;
    });
  };

  return {
    can,
    canView,
    canEdit,
    canAny,
    canAll,
    isRole,
    userPermissions,
    userRole,
  };
}
